import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import ChipAIGenerated from "../Chip/ChipAIGenerated";
import { useAIGeneratedLeadFormDraft } from "../../hooks/useAIGeneratedSection";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import ContainerModalActions from "../Containers/ContainerModalActions";
import ModalBasicLayout, {
  ModalActionsPortal,
} from "../Modal/ModalBasicLayout";
import TextField from "../Form/TextField";
import Select, { SELECT_MENU_ABOVE_MODAL_PROPS } from "../Form/Select";
import Checkbox from "../Form/Checkbox";
import {
  AddLinkButton,
  CustomQuestionFields,
  FieldWithExternalLabel,
  FormSection,
  SectionTitleWithCounter,
} from "../LeadForm/LeadFormFields";
import { MULTILINE_RESIZE_SX } from "../LeadForm/leadFormStyles";
import { SimpleAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import { startCreateInstantForm } from "../../actions/adsCampaigns";
import RecommendedLeadFormNotice from "../LeadForm/RecommendedLeadFormNotice";
import {
  useApplyRecommendedLeadForm,
  useRecommendedLeadForm,
} from "../../hooks/useRecommendedLeadForm";
import { REGEX_URL_WITH_PROTOCOL_REQUIRED } from "../../utils/regex";
import { META } from "../../utils/constants";
import {
  DEFAULT_META_INSTANT_FORM_CONTACT_FIELDS,
  MAX_META_INSTANT_FORM_QUESTIONS,
  META_INSTANT_FORM_ACTION_BOOK_TIME,
  META_INSTANT_FORM_ACTION_VIEW_WEBSITE,
  META_INSTANT_FORM_CONTACT_FIELD_OPTIONS,
  META_INSTANT_FORM_LOCALE_OPTIONS,
  META_INSTANT_FORM_TYPE_HIGHER_INTENT,
  META_INSTANT_FORM_TYPE_MORE_VOLUME,
  detectSchedulingProvider,
  META_INSTANT_FORM_FIELD_LIMITS,
  resolveMetaInstantFormLocale,
} from "../../utils/metaInstantForm";
import {
  BUTTON_ADD,
  BUTTON_CREATE,
  BUTTON_DUPLICATE,
  BUTTON_GO_BACK,
  ERROR_OCCURRED,
  FIELD_MAX_LENGTH,
  FIELD_REQUIRED,
  FIELD_URL_VALID_WITH_PROTOCOL,
  INSTANT_FORM_FIELD_DESCRIPTION,
  INSTANT_FORM_SECTION_END,
  INSTANT_FORM_SECTION_FORM_TYPE,
  INSTANT_FORM_SECTION_INTRO,
  INSTANT_FORM_SECTION_QUESTIONS,
  LANGUAGE_PLACEHOLDER,
  MODAL_CREATE_INSTANT_FORM_ACTION_BOOK_TIME,
  MODAL_CREATE_INSTANT_FORM_ACTION_BOOK_TIME_DESCRIPTION,
  MODAL_CREATE_INSTANT_FORM_ACTION_VIEW_WEBSITE,
  MODAL_CREATE_INSTANT_FORM_ACTION_VIEW_WEBSITE_DESCRIPTION,
  MODAL_CREATE_INSTANT_FORM_ADDITIONAL_ACTION,
  MODAL_CREATE_INSTANT_FORM_BOOK_TIME_HINT,
  MODAL_CREATE_INSTANT_FORM_BUTTON_TEXT,
  MODAL_CREATE_INSTANT_FORM_CONTACT_FIELDS,
  MODAL_CREATE_INSTANT_FORM_DESCRIPTION,
  MODAL_CREATE_INSTANT_FORM_MAX_QUESTIONS,
  MODAL_CREATE_INSTANT_FORM_PRIVACY_LINK_TEXT,
  MODAL_CREATE_INSTANT_FORM_QUESTIONS_HEADLINE,
  MODAL_CREATE_INSTANT_FORM_SCHEDULING_LINK,
  MODAL_CREATE_INSTANT_FORM_SCHEDULING_PROVIDER,
  MODAL_CREATE_INSTANT_FORM_TITLE,
  MODAL_CREATE_INSTANT_FORM_TYPE_HIGHER_INTENT,
  MODAL_CREATE_INSTANT_FORM_TYPE_HIGHER_INTENT_DESCRIPTION,
  MODAL_CREATE_INSTANT_FORM_TYPE_MORE_VOLUME,
  MODAL_CREATE_INSTANT_FORM_TYPE_MORE_VOLUME_DESCRIPTION,
  MODAL_CREATE_INSTANT_FORM_WEBSITE_URL,
  MODAL_CREATE_LEAD_FORM_CUSTOM_QUESTIONS,
  MODAL_CREATE_LEAD_FORM_HEADLINE,
  MODAL_CREATE_LEAD_FORM_NAME,
  MODAL_CREATE_LEAD_FORM_PRIVACY_POLICY_URL,
  MODAL_CREATE_LEAD_FORM_SECTION_FORM,
  MODAL_CREATE_LEAD_FORM_SECTION_PRIVACY,
  MODAL_LEAD_FORMS_COPY_NAME_PREFIX,
  MODAL_VIEW_LEAD_FORM_DESCRIPTION,
  MODAL_VIEW_LEAD_FORM_TITLE,
} from "../../i18n/keysTranslations";

const FORM_TYPE_OPTIONS = [
  {
    value: META_INSTANT_FORM_TYPE_MORE_VOLUME,
    label: MODAL_CREATE_INSTANT_FORM_TYPE_MORE_VOLUME,
    description: MODAL_CREATE_INSTANT_FORM_TYPE_MORE_VOLUME_DESCRIPTION,
  },
  {
    value: META_INSTANT_FORM_TYPE_HIGHER_INTENT,
    label: MODAL_CREATE_INSTANT_FORM_TYPE_HIGHER_INTENT,
    description: MODAL_CREATE_INSTANT_FORM_TYPE_HIGHER_INTENT_DESCRIPTION,
  },
];

const THANK_YOU_ACTION_OPTIONS = [
  {
    value: META_INSTANT_FORM_ACTION_VIEW_WEBSITE,
    label: MODAL_CREATE_INSTANT_FORM_ACTION_VIEW_WEBSITE,
    description: MODAL_CREATE_INSTANT_FORM_ACTION_VIEW_WEBSITE_DESCRIPTION,
  },
  {
    value: META_INSTANT_FORM_ACTION_BOOK_TIME,
    label: MODAL_CREATE_INSTANT_FORM_ACTION_BOOK_TIME,
    description: MODAL_CREATE_INSTANT_FORM_ACTION_BOOK_TIME_DESCRIPTION,
  },
];

const {
  name: NAME_MAX_LENGTH,
  headline: HEADLINE_MAX_LENGTH,
  body: BODY_MAX_LENGTH,
  privacyLinkText: PRIVACY_LINK_TEXT_MAX_LENGTH,
  buttonText: BUTTON_TEXT_MAX_LENGTH,
  url: URL_MAX_LENGTH,
} = META_INSTANT_FORM_FIELD_LIMITS;

const RadioOptionsField = ({ name, control, options, disabled, t }) => (
  <Controller
    name={name}
    control={control}
    render={({ field: { value, onChange } }) => (
      <RadioGroup
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={{ gap: 1 }}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            disabled={disabled}
            control={<Radio sx={{ p: 0, mr: 1 }} />}
            label={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2">{t(option.label)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(option.description)}
                </Typography>
              </Box>
            }
            sx={{ ml: 0, mr: 0, alignItems: "flex-start" }}
          />
        ))}
      </RadioGroup>
    )}
  />
);

const ModalCreateInstantForm = ({
  modalOpen,
  onCloseModal = () => {},
  onCallbackSave = () => {},
  integrationID,
  pageID,
  initialValues = null,
  campaignLanguage,
  readOnly = false,
  leadFormRecommendation = null,
  aiGeneratedDraft = false,
}) => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const isReadOnly = readOnly && !isDuplicating;

  const {
    control,
    getValues,
    formState,
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      locale: resolveMetaInstantFormLocale(campaignLanguage ?? i18n?.language),
      formType: META_INSTANT_FORM_TYPE_MORE_VOLUME,
      introHeadline: "",
      introDescription: "",
      questionsHeadline: "",
      contactFields: DEFAULT_META_INSTANT_FORM_CONTACT_FIELDS,
      customQuestions: [],
      privacyPolicyUrl: "",
      privacyPolicyLinkText: "",
      thankYouTitle: "",
      thankYouBody: "",
      thankYouAction: META_INSTANT_FORM_ACTION_VIEW_WEBSITE,
      thankYouButtonText: "",
      thankYouUrl: "",
      ...(initialValues || {}),
    },
  });

  useFocusErrorForm(formState);
  const showAIGeneratedDraft = useAIGeneratedLeadFormDraft({
    enabled: aiGeneratedDraft,
    control,
    getValues,
  });
  const { errors } = formState;
  const recommendation = useRecommendedLeadForm({
    platform: META,
    adID: leadFormRecommendation?.adID,
    adGroupID: leadFormRecommendation?.adGroupID,
    enabled: Boolean(leadFormRecommendation) && !initialValues && !isReadOnly,
    campaignID: leadFormRecommendation?.campaignID,
    advertiserID: leadFormRecommendation?.advertiserID,
    language: leadFormRecommendation?.language ?? campaignLanguage,
    getCampaignValues: leadFormRecommendation?.getCampaignValues,
    setCampaignValue: leadFormRecommendation?.setCampaignValue,
  });
  useApplyRecommendedLeadForm({ recommendation, reset, getValues });
  const isGeneratingRecommendation = recommendation.isGenerating;
  const isEditorLocked =
    isSubmitting || isReadOnly || isGeneratingRecommendation;
  const selectedContactFields = watch("contactFields") || [];
  const thankYouAction = useWatch({ control, name: "thankYouAction" });
  const thankYouUrl = useWatch({ control, name: "thankYouUrl" });
  const isBookTime = thankYouAction === META_INSTANT_FORM_ACTION_BOOK_TIME;
  const schedulingProvider = isBookTime
    ? detectSchedulingProvider(thankYouUrl)
    : null;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "customQuestions",
  });

  const contactFieldsCount = Array.isArray(selectedContactFields)
    ? selectedContactFields.length
    : 0;
  const customQuestionsCount = fields.length;
  const maxContactFields = Math.max(
    0,
    MAX_META_INSTANT_FORM_QUESTIONS - customQuestionsCount
  );
  const maxCustomQuestions = Math.max(
    0,
    MAX_META_INSTANT_FORM_QUESTIONS - contactFieldsCount
  );
  const isQuestionsLimitReached =
    contactFieldsCount + customQuestionsCount >=
    MAX_META_INSTANT_FORM_QUESTIONS;

  const urlRules = useMemo(
    () => ({
      required: {
        value: true,
        message: t(FIELD_REQUIRED),
      },
      maxLength: {
        value: URL_MAX_LENGTH,
        message: t(FIELD_MAX_LENGTH, { value: URL_MAX_LENGTH }),
      },
      pattern: {
        value: REGEX_URL_WITH_PROTOCOL_REQUIRED,
        message: t(FIELD_URL_VALID_WITH_PROTOCOL),
      },
    }),
    [t]
  );

  const getMaxLengthRule = (value, required = false) => ({
    ...(required
      ? {
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
        }
      : {}),
    maxLength: {
      value,
      message: t(FIELD_MAX_LENGTH, { value }),
    },
  });

  const onAddCustomQuestion = () => {
    if (isReadOnly) return;
    if (customQuestionsCount >= maxCustomQuestions) return;
    append({
      question: "",
      type: "TEXT",
      options: ["", ""],
    });
  };

  const onSubmit = async () => {
    if (isEditorLocked) return;
    setIsSubmitting(true);
    try {
      const response = await dispatch(
        startCreateInstantForm({
          integrationID,
          pageID,
          platform: META,
          formData: getValues(),
        })
      );

      if (!response?.ok || !response?.data?.id) {
        SimpleAlert({
          icon: "error",
          title: t(ERROR_OCCURRED),
          text: response?.message || t(ERROR_OCCURRED),
        });
        return;
      }

      onCallbackSave(response.data);
      onCloseModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={isSubmitting ? () => {} : onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="sm"
      title={t(
        isReadOnly
          ? MODAL_VIEW_LEAD_FORM_TITLE
          : MODAL_CREATE_INSTANT_FORM_TITLE
      )}
      description={t(
        isReadOnly
          ? MODAL_VIEW_LEAD_FORM_DESCRIPTION
          : MODAL_CREATE_INSTANT_FORM_DESCRIPTION
      )}
      showCloseButton={!isSubmitting}
    >
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isEditorLocked) return;
          handleSubmit(onSubmit)();
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <RecommendedLeadFormNotice
          isGenerating={isGeneratingRecommendation}
          notice={recommendation.notice}
          onUseDefaults={recommendation.onUseDefaults}
        />
        {!isGeneratingRecommendation ? (
          <>
            {showAIGeneratedDraft ? <ChipAIGenerated /> : null}
            <Box
              component="fieldset"
              disabled={isEditorLocked}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                m: 0,
                p: 0,
                minWidth: 0,
                border: "none",
              }}
            >
              <FormSection
                title={t(MODAL_CREATE_LEAD_FORM_SECTION_FORM)}
                showDivider={false}
              >
                <FieldWithExternalLabel label={t(MODAL_CREATE_LEAD_FORM_NAME)}>
                  <TextField
                    label=""
                    name="name"
                    variant="filled"
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={getMaxLengthRule(NAME_MAX_LENGTH, true)}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel label={t(LANGUAGE_PLACEHOLDER)}>
                  <Select
                    label=""
                    name="locale"
                    control={control}
                    errors={errors}
                    options={META_INSTANT_FORM_LOCALE_OPTIONS}
                    doTranslate={true}
                    fullWidth
                    variant="filled"
                    disabled={isReadOnly}
                    MenuProps={SELECT_MENU_ABOVE_MODAL_PROPS}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(INSTANT_FORM_SECTION_FORM_TYPE)}
                >
                  <RadioOptionsField
                    name="formType"
                    control={control}
                    options={FORM_TYPE_OPTIONS}
                    disabled={isReadOnly}
                    t={t}
                  />
                </FieldWithExternalLabel>
              </FormSection>
              <FormSection title={t(INSTANT_FORM_SECTION_INTRO)}>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_LEAD_FORM_HEADLINE)}
                >
                  <TextField
                    label=""
                    name="introHeadline"
                    variant="filled"
                    showCounterCharacters
                    maxCharacters={HEADLINE_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={getMaxLengthRule(HEADLINE_MAX_LENGTH)}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(INSTANT_FORM_FIELD_DESCRIPTION)}
                >
                  <TextField
                    label=""
                    name="introDescription"
                    variant="filled"
                    multiline
                    minRows={2}
                    maxRows={6}
                    showCounterCharacters
                    maxCharacters={BODY_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    sx={MULTILINE_RESIZE_SX}
                    rules={getMaxLengthRule(BODY_MAX_LENGTH)}
                  />
                </FieldWithExternalLabel>
              </FormSection>
              <FormSection title={t(INSTANT_FORM_SECTION_QUESTIONS)}>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_INSTANT_FORM_QUESTIONS_HEADLINE)}
                >
                  <TextField
                    label=""
                    name="questionsHeadline"
                    variant="filled"
                    showCounterCharacters
                    maxCharacters={HEADLINE_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={getMaxLengthRule(HEADLINE_MAX_LENGTH)}
                  />
                </FieldWithExternalLabel>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    pl: "2px",
                  }}
                >
                  <SectionTitleWithCounter
                    title={t(MODAL_CREATE_INSTANT_FORM_CONTACT_FIELDS)}
                    count={contactFieldsCount}
                    max={maxContactFields}
                    sx={{ display: "block", pb: "8px" }}
                  />
                  <Controller
                    name="contactFields"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!Array.isArray(value) || value.length === 0) {
                          return t(FIELD_REQUIRED);
                        }
                        if (
                          value.length + customQuestionsCount >
                          MAX_META_INSTANT_FORM_QUESTIONS
                        ) {
                          return t(MODAL_CREATE_INSTANT_FORM_MAX_QUESTIONS, {
                            value: MAX_META_INSTANT_FORM_QUESTIONS,
                          });
                        }
                        return true;
                      },
                    }}
                    render={({ field: { value = [], onChange } }) => (
                      <Box
                        id="contactFields"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {META_INSTANT_FORM_CONTACT_FIELD_OPTIONS.map(
                          (option) => {
                            const isChecked = value.includes(option.value);
                            const isDisabled =
                              isReadOnly ||
                              (!isChecked && value.length >= maxContactFields);
                            return (
                              <FormControlLabel
                                key={option.value}
                                control={
                                  <Checkbox
                                    checked={isChecked}
                                    disabled={isDisabled}
                                    sx={{ p: 0, mr: 1 }}
                                    onChange={() => {
                                      if (isChecked) {
                                        onChange(
                                          value.filter(
                                            (field) => field !== option.value
                                          )
                                        );
                                        return;
                                      }
                                      if (value.length >= maxContactFields)
                                        return;
                                      onChange([...value, option.value]);
                                    }}
                                  />
                                }
                                label={t(option.label)}
                                sx={{
                                  ml: 0,
                                  mr: 0,
                                  alignItems: "center",
                                }}
                              />
                            );
                          }
                        )}
                        {errors?.contactFields?.message && (
                          <Typography variant="body2" color="error">
                            {errors.contactFields.message}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    mt: 1.5,
                    pl: "2px",
                  }}
                >
                  <SectionTitleWithCounter
                    title={t(MODAL_CREATE_LEAD_FORM_CUSTOM_QUESTIONS)}
                    count={customQuestionsCount}
                    max={maxCustomQuestions}
                  />
                  {fields.map((item, index) => (
                    <CustomQuestionFields
                      key={item.id}
                      index={index}
                      control={control}
                      errors={errors}
                      onRemove={() => remove(index)}
                      t={t}
                      readOnly={isReadOnly}
                      setValue={setValue}
                      getValues={getValues}
                    />
                  ))}
                  {!isReadOnly && (
                    <AddLinkButton
                      onClick={onAddCustomQuestion}
                      disabled={customQuestionsCount >= maxCustomQuestions}
                    >
                      {t(BUTTON_ADD)}
                    </AddLinkButton>
                  )}
                  {isQuestionsLimitReached && (
                    <Typography variant="body2" color="text.secondary">
                      {t(MODAL_CREATE_INSTANT_FORM_MAX_QUESTIONS, {
                        value: MAX_META_INSTANT_FORM_QUESTIONS,
                      })}
                    </Typography>
                  )}
                </Box>
              </FormSection>
              <FormSection title={t(MODAL_CREATE_LEAD_FORM_SECTION_PRIVACY)}>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_LEAD_FORM_PRIVACY_POLICY_URL)}
                >
                  <TextField
                    label=""
                    name="privacyPolicyUrl"
                    variant="filled"
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={urlRules}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_INSTANT_FORM_PRIVACY_LINK_TEXT)}
                >
                  <TextField
                    label=""
                    name="privacyPolicyLinkText"
                    variant="filled"
                    showCounterCharacters
                    maxCharacters={PRIVACY_LINK_TEXT_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={getMaxLengthRule(PRIVACY_LINK_TEXT_MAX_LENGTH)}
                  />
                </FieldWithExternalLabel>
              </FormSection>
              <FormSection title={t(INSTANT_FORM_SECTION_END)}>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_LEAD_FORM_HEADLINE)}
                >
                  <TextField
                    label=""
                    name="thankYouTitle"
                    variant="filled"
                    showCounterCharacters
                    maxCharacters={HEADLINE_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={getMaxLengthRule(HEADLINE_MAX_LENGTH, true)}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(INSTANT_FORM_FIELD_DESCRIPTION)}
                >
                  <TextField
                    label=""
                    name="thankYouBody"
                    variant="filled"
                    multiline
                    minRows={2}
                    maxRows={6}
                    showCounterCharacters
                    maxCharacters={BODY_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    sx={MULTILINE_RESIZE_SX}
                    rules={getMaxLengthRule(BODY_MAX_LENGTH)}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_INSTANT_FORM_ADDITIONAL_ACTION)}
                >
                  <RadioOptionsField
                    name="thankYouAction"
                    control={control}
                    options={THANK_YOU_ACTION_OPTIONS}
                    disabled={isReadOnly}
                    t={t}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(
                    isBookTime
                      ? MODAL_CREATE_INSTANT_FORM_SCHEDULING_LINK
                      : MODAL_CREATE_INSTANT_FORM_WEBSITE_URL
                  )}
                >
                  <TextField
                    label=""
                    name="thankYouUrl"
                    variant="filled"
                    fullWidth
                    placeholder={
                      isBookTime ? "https://calendly.com/..." : "https://"
                    }
                    control={control}
                    errors={errors}
                    rules={urlRules}
                  />
                  {isBookTime && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                        pl: "2px",
                      }}
                    >
                      {schedulingProvider && (
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {t(MODAL_CREATE_INSTANT_FORM_SCHEDULING_PROVIDER, {
                            provider: schedulingProvider,
                          })}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t(MODAL_CREATE_INSTANT_FORM_BOOK_TIME_HINT)}
                      </Typography>
                    </Box>
                  )}
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_INSTANT_FORM_BUTTON_TEXT)}
                >
                  <TextField
                    label=""
                    name="thankYouButtonText"
                    variant="filled"
                    showCounterCharacters
                    maxCharacters={BUTTON_TEXT_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={getMaxLengthRule(BUTTON_TEXT_MAX_LENGTH, true)}
                  />
                </FieldWithExternalLabel>
              </FormSection>
            </Box>
          </>
        ) : null}
      </Box>
      {!isGeneratingRecommendation ? (
        <ModalActionsPortal>
          <Box
            sx={{
              px: { xs: 3, sm: 5 },
              pt: 1.5,
              pb: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <ContainerModalActions
              hasPadding={false}
              propsSecondaryButton={{
                showButton: true,
                onClick: onCloseModal,
                text: BUTTON_GO_BACK,
                disabled: isSubmitting,
              }}
              propsTertiaryButton={{
                showButton: isReadOnly,
                type: "button",
                text: BUTTON_DUPLICATE,
                disabled: isSubmitting,
                onClick: () => {
                  const prefix = t(MODAL_LEAD_FORMS_COPY_NAME_PREFIX);
                  const currentName = String(getValues("name") || "").trim();
                  const alreadyPrefixed = currentName
                    .toLowerCase()
                    .startsWith(`${prefix.toLowerCase()} `);
                  setValue(
                    "name",
                    !currentName || alreadyPrefixed
                      ? currentName || prefix
                      : `${prefix} ${currentName}`,
                    { shouldDirty: true }
                  );
                  setIsDuplicating(true);
                },
              }}
              propsPrimaryButton={{
                showButton: !isReadOnly,
                text: BUTTON_CREATE,
                type: "button",
                loading: isSubmitting,
                disabled: isEditorLocked,
                onClick: async (event) => {
                  event.preventDefault();
                  if (isEditorLocked) return;
                  await handleSubmit(onSubmit)();
                },
              }}
            />
          </Box>
        </ModalActionsPortal>
      ) : null}
    </ModalBasicLayout>
  );
};

export default ModalCreateInstantForm;
